import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import EmptyList from 'components/EmptyList/EmptyList';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import StatusDropButton from 'components/StatusDropButton/StatusDropButton';
import Tabs from 'components/Tabs/Tabs';
import TextField from 'components/TextField/TextField';
import { TASK_HEAD_IRO_MAIN_CELLS } from 'constants/dataSets';
import { SEARCH, STATUS } from 'constants/formFields';
import {
  CATEGORY_IMPACT_TEMPLATE, IRO_CATEGORY_TEMPLATE, MODALS_IRO_IDENTIFICATION, TABS_IRO_IDENTIFICATION_ADMIN,
} from 'constants/general';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';
import CreateCustomIROItem from 'pages/MaterialAssessmentPages/MaterialAssessmentIROIndetificator/components/CreateCustomIROItem/CreateCustomIROItem';

import ItemsTableMain from './components/ItemsTableMain/ItemsTableMain';

import type IStandardsIRO from './interface/IStandardsIRO';

import styles from './scss/StandardsIRO.module.scss';

function StandardsIROView({
  loading, activeTab, handleClickTab, items,
  isDeleteModalOpen, handleDeleteTemplate, handleDeleteConfirm, handleCloseModal,
  hasMore, onLoadMore, handleEdit, selectEditItem, openModal, naceCodeTreeData,
  handleApprove, handleReject, updateIROItem, standards, control, setOffset,
  onSearchChange, searchValue,
}: IStandardsIRO) {
  const { t } = useTranslation();

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  return (
    <div className={styles.root}>
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <div className={styles.headerWrapper}>
            <span className={styles.title}>{t('materialityAssessment.titleIROIdentification')}</span>
            <Controller
              control={control}
              name={STATUS}
              render={({ field: { value, onChange } }) => (
                <StatusDropButton
                  options={IRO_CATEGORY_TEMPLATE}
                  title={t('common.category')}
                  selectedOptions={value?.filter((item): item is string => item !== undefined)}
                  onSelectionChange={(event) => {
                    onChange(event);
                    setOffset(0);
                  }}
                  className={styles.buttonStatus}
                  classNameList={styles.buttonStatusList}
                  clearAllSelected={() => {
                    setOffset(0);
                    onChange([]);
                  }}
                  hideSearch
                />
              )}
            />
          </div>
          <div className={styles.contentWrapper}>
            <Tabs
              data={TABS_IRO_IDENTIFICATION_ADMIN}
              activeTab={activeTab}
              onClickTab={handleClickTab}
            />
            {activeTab !== 2 ? (
              <div className={styles.search}>
                <Controller
                  control={control}
                  name={SEARCH}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <TextField
                      name={name}
                      value={value}
                      placeholder={t('common.searchByName')}
                      error={error?.message}
                      icon={<SearchIcon />}
                      onChange={(event) => {
                        onChange(event.target.value);
                        onSearchChange({ search: event.target.value, onChange });
                      }}
                      required
                    />
                  )}
                />
              </div>
            ) : null}
            <div className={styles.content}>
              {loading && items.length === 0 ? (<ContentLoader title={t('materialityAssessment.loadingIRO')} />) : null}
              {items.length === 0 && !loading && !searchValue ? (
                <EmptyTaskManagement
                  title={t(
                    'materialityAssessment.emptyIRODescription',
                    { name: CATEGORY_IMPACT_TEMPLATE.IMPACT },
                  )}
                  checkIcon
                />
              ) : null}
              {items.length === 0 && !loading && searchValue ? (
                <EmptyList text={emptyListText} />
              ) : null}
              {items.length > 0 && (
                <div className={styles.tableWrapper}>
                  <ItemsTableMain
                    items={items}
                    headCells={TASK_HEAD_IRO_MAIN_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={loading}
                    naceCodeTreeData={naceCodeTreeData}
                    deleteTemplate={handleDeleteTemplate}
                    handleEdit={handleEdit}
                    showBtn={activeTab === 2}
                    handleApprove={handleApprove}
                    handleReject={handleReject}
                    isInfoTable
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <CreateCustomIROItem
          open={openModal === MODALS_IRO_IDENTIFICATION.ADD_IMPACT
            || openModal === MODALS_IRO_IDENTIFICATION.ADD_RISKS
            || openModal === MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY}
          handleCancel={handleCloseModal}
          title={selectEditItem ? t(
            'materialityAssessment.editTemplate',
            { name: openModal },
          ) : t(
            'materialityAssessment.addCustomTemplate',
            { name: openModal },
          )}
          standards={standards}
          selectEditItem={selectEditItem}
          naceCodeTreeData={naceCodeTreeData}
          updateIROItem={updateIROItem}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          onReject={handleCloseModal}
          isLoading={false}
          name=""
          title={CATEGORY_IMPACT_TEMPLATE.TEMPLATE}
          hint={CATEGORY_IMPACT_TEMPLATE.HINT_TEMPLATE}
          onConfirm={handleDeleteConfirm}
          hintClassName={styles.hint}
        />
      </div>
    </div>
  );
}

export default StandardsIROView;
